import { useMemo } from 'react'
import { useReadContracts } from 'wagmi'
import { ADDRESSES, IS_LIVE } from '@/lib/contracts'
import veAbi from '@/lib/abis/HorizonVoteEscrow.json'
import { POLL_SLOW } from '@/lib/utils'

const ZERO = '0x0000000000000000000000000000000000000000'
const VE_NOT_DEPLOYED = !IS_LIVE || (ADDRESSES.HORIZON_VOTE_ESCROW || '').toLowerCase() === ZERO

/// A holder's veNFT positions, read straight off HorizonVoteEscrow (the ve(3,3) rewrite: a lock is an
/// ERC-721 tokenId, so one wallet holds MANY, each with its own amount, unlock date and power).
/// Three chained multicalls, each enabled only once the prior resolves:
///   1. `balanceOf` (token COUNT, not power in the NFT model) + `votingPowerOf` + `totalPower`.
///   2. `tokenOfOwnerByIndex` for each held token -> the id list.
///   3. `locked(id)` (amount, end, isBurn) + `balanceOfNFT(id)` (this token's live power) per id.
/// Returns the position array plus the wallet's aggregate power and its share of all power.
export function useVeNFTs(user) {
  const ve = ADDRESSES.HORIZON_VOTE_ESCROW
  const enabled = !VE_NOT_DEPLOYED && !!user

  const { data: base, refetch: refetchBase } = useReadContracts({
    query: { enabled, refetchInterval: POLL_SLOW },
    contracts: [
      { address: ve, abi: veAbi, functionName: 'balanceOf', args: [user ?? ZERO] },
      { address: ve, abi: veAbi, functionName: 'votingPowerOf', args: [user ?? ZERO] },
      { address: ve, abi: veAbi, functionName: 'totalPower' },
    ],
  })
  const nftCount = Number(base?.[0]?.result ?? 0n)
  const userPower = base?.[1]?.result ?? 0n
  const totalPower = base?.[2]?.result ?? 0n

  const { data: idReads, refetch: refetchIds } = useReadContracts({
    query: { enabled: enabled && nftCount > 0, refetchInterval: POLL_SLOW },
    contracts: Array.from({ length: nftCount }, (_, i) => ({
      address: ve, abi: veAbi, functionName: 'tokenOfOwnerByIndex', args: [user, BigInt(i)],
    })),
  })
  const ids = useMemo(
    () => (idReads ?? []).map((r) => r?.result).filter((x) => x != null),
    [idReads],
  )

  const { data: posReads, refetch: refetchPos } = useReadContracts({
    query: { enabled: enabled && ids.length > 0, refetchInterval: POLL_SLOW },
    contracts: ids.flatMap((id) => [
      { address: ve, abi: veAbi, functionName: 'locked', args: [id] },
      { address: ve, abi: veAbi, functionName: 'balanceOfNFT', args: [id] },
    ]),
  })

  const positions = useMemo(() => ids.map((id, i) => {
    const locked = posReads?.[i * 2]?.result
    const power = posReads?.[i * 2 + 1]?.result ?? 0n
    return {
      id,
      amount: locked?.[0] ?? 0n,
      end: Number(locked?.[1] ?? 0n),
      isBurn: !!locked?.[2],
      power,
    }
  }), [ids, posReads])

  const refetch = async () => {
    await Promise.all([refetchBase(), refetchIds(), refetchPos()].map((p) => p?.catch?.(() => {}) ?? p))
  }

  return { positions, nftCount, userPower, totalPower, loaded: !!base, refetch }
}
